import poppinsSemiBoldLatinWoff2 from '@fontsource/poppins/files/poppins-latin-600-normal.woff2?url'
import figtreeLatinWoff2 from '@fontsource-variable/figtree/files/figtree-latin-wght-normal.woff2?url'
import type { QueryClient } from '@tanstack/react-query'
import {
  createRootRouteWithContext,
  HeadContent,
  Scripts,
  useRouterState,
} from '@tanstack/react-router'
import { IconCompassOutline18, IconHouse4Outline18 } from 'nucleo-ui-outline-18'
import { lazy, Suspense, useRef } from 'react'

import { MarketingLink } from '~/components/marketing/marketing-link'
import { buttonVariants } from '~/components/ui/button'
import { APP_VERSION_META_NAME, CLIENT_APP_VERSION } from '~/lib/app-version'
import { GOOGLE_ADS_ID } from '~/lib/gtag'
import { isMarketingPath } from '~/lib/marketing-paths'
import {
  DEFAULT_DOCUMENT_DESCRIPTION,
  DEFAULT_DOCUMENT_TITLE,
  FALLBACK_TITLES,
} from '~/lib/page-title'
import { cn } from '~/lib/utils'
import appCss from '../styles.css?url'

// Lazy so the marketing surface never downloads the app providers' weight
// (Lingui catalogs, upload store, toast/tooltip runtime) — see the branch
// in `RootDocument` and `src/lib/marketing-paths.ts`.
const AppShellProviders = lazy(() =>
  import('~/components/app-shell-providers').then((m) => ({ default: m.AppShellProviders }))
)

/** Runs before first paint to apply the saved theme and avoid a flash. */
const themeScript = `(function(){try{var t=localStorage.getItem('planno-theme')||'system';var d=t==='dark'||(t!=='light'&&matchMedia('(prefers-color-scheme: dark)').matches);var e=document.documentElement;e.classList.toggle('dark',d);e.style.colorScheme=d?'dark':'light';}catch(e){}})()`

/**
 * Runs before first paint to set `<html lang>` AND the fallback document
 * title from the saved locale, so neither is briefly wrong for a visitor
 * whose language isn't the pt-BR default.
 *
 * The title half only covers the FALLBACK — routes that set no title of
 * their own. Most app routes do set one, and the server renders it in the
 * default locale because it cannot know the visitor's:
 * `@tanstack/react-start/server` exposes only handler plumbing, with no
 * cookie or header accessor reachable from `head()`. Those per-page titles
 * are corrected a moment later, when `getAppRouter()` activates the real
 * locale and `head()` re-renders — so an English visitor may see the
 * Portuguese title for one frame. Removing that flash would need the server
 * to know the locale, which this framework version does not allow.
 *
 * The two titles below must be literals: no module has loaded yet, so the
 * script cannot call `i18n._()`.
 *
 * Skipped on the marketing surface, whose language is decided by the URL and
 * baked into the prerendered HTML (`/` is pt-BR, `/en/**` is English) — not by
 * the visitor's stored app preference. Without the guard, a signed-in pt-BR
 * user opening `/en` would get `lang="pt-BR"` on an English document, which
 * mis-cues screen readers and hyphenation, and its per-page title would be
 * stomped. The pattern mirrors `MARKETING_EXACT_PATHS` + `MARKETING_PREFIXES`
 * in `src/lib/marketing-paths.ts`; keep the two in step when a marketing
 * route is added.
 */
const localeScript = `(function(){try{var p=location.pathname.replace(/(.)\\/$/,'$1');if(/^\\/(?:$|en(?:\\/|$)|pt(?:\\/|$)|recursos\\/|jornada$|termos-de-uso$|politica-de-privacidade$)/.test(p)){return;}var q=new URLSearchParams(location.search).get('lang');var l=(q==='pt-BR'||q==='en')?q:localStorage.getItem('planno-locale');if(l!=='pt-BR'&&l!=='en'){return;}document.documentElement.lang=l;var t=${JSON.stringify(FALLBACK_TITLES)};if(document.title===${JSON.stringify(DEFAULT_DOCUMENT_TITLE)}){document.title=t[l];}}catch(e){}})()`

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient
}>()({
  head: () => ({
    links: [
      /* Preload the latin subsets used above the fold so the fonts arrive
         before first paint instead of after the CSS → @font-face waterfall,
         avoiding the fallback→webfont swap on cold caches. */
      {
        as: 'font',
        crossOrigin: 'anonymous',
        href: figtreeLatinWoff2,
        rel: 'preload',
        type: 'font/woff2',
      },
      {
        as: 'font',
        crossOrigin: 'anonymous',
        href: poppinsSemiBoldLatinWoff2,
        rel: 'preload',
        type: 'font/woff2',
      },
      /* Warm the Convex connection while the JS is still downloading. The
         client can't open its socket until `app-router` has been imported
         and React has mounted the auth provider — by which point DNS, TCP
         and TLS are all still ahead of it, on the critical path to the first
         authenticated render. This moves that handshake off it.

         It costs anonymous visitors one speculative connection they never
         use: the marketing router carries no Convex client at all. A single
         idle socket the browser drops after a few seconds is worth trading
         for a full round trip on every signed-in load. */
      { href: import.meta.env.VITE_CONVEX_URL, rel: 'preconnect' },
      { href: appCss, rel: 'stylesheet' },
      { href: '/favicon.ico', rel: 'icon', type: 'image/x-icon' },
      { href: '/favicon.svg', rel: 'icon', type: 'image/svg+xml' },
      { href: '/apple-touch-icon.png', rel: 'apple-touch-icon', sizes: '180x180' },
    ],
    meta: [
      {
        charSet: 'utf-8',
      },
      {
        content: 'width=device-width, initial-scale=1',
        name: 'viewport',
      },
      {
        title: DEFAULT_DOCUMENT_TITLE,
      },
      {
        content: DEFAULT_DOCUMENT_DESCRIPTION,
        name: 'description',
      },
      {
        content: '#ffffff',
        media: '(prefers-color-scheme: light)',
        name: 'theme-color',
      },
      {
        content: '#0b0b0d',
        media: '(prefers-color-scheme: dark)',
        name: 'theme-color',
      },
      ...(CLIENT_APP_VERSION
        ? [
            {
              content: CLIENT_APP_VERSION,
              name: APP_VERSION_META_NAME,
            },
          ]
        : []),
    ],
    // Google Ads tag (gtag.js), site-wide per Google's recommended setup:
    // captures ad-click ids on landing, builds remarketing audiences, and
    // lets in-app conversion events fire anywhere. Only rendered when
    // VITE_GOOGLE_ADS_ID is set — see docs/ads/conversion-tracking.md.
    scripts: GOOGLE_ADS_ID
      ? [
          { async: true, src: `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}` },
          {
            children: `window.dataLayer=window.dataLayer||[];function gtag(){dataLayer.push(arguments);}gtag('js',new Date());gtag('config','${GOOGLE_ADS_ID}');`,
          },
        ]
      : [],
  }),
  // Hardcoded pt-BR (not Lingui): this can render on the provider-free
  // marketing branch of `RootDocument`, where there is no `I18nProvider`.
  notFoundComponent: () => (
    <main className="flex min-h-svh flex-col items-center justify-center gap-4 bg-canvas px-6 text-center">
      <div className="flex size-12 items-center justify-center rounded-full bg-muted text-muted-foreground">
        <IconCompassOutline18 className="size-6" />
      </div>
      <div className="space-y-1">
        <p className="font-semibold text-2xl tracking-tight">Página não encontrada</p>
        <p className="text-muted-foreground text-sm">A página que você procura não existe.</p>
      </div>
      <MarketingLink className={cn(buttonVariants(), 'mt-1')} href="/">
        <IconHouse4Outline18 className="size-4" />
        Voltar para o início
      </MarketingLink>
    </main>
  ),
  shellComponent: RootDocument,
})

function RootDocument({ children }: { children: React.ReactNode }) {
  // Marketing pages render bare — no Lingui, Sentry, upload store, toasts —
  // so anonymous visitors never download the app shell. The branch must
  // agree between the prerender and the client, which it does: both resolve
  // the same pathname. App routes are `ssr: false`, so the Suspense
  // fallback while the providers chunk loads is invisible in practice.
  //
  // Both locations are consulted because they move at different times, and
  // keying on either one alone tears the providers off a route that is still
  // rendering: `location` updates the instant a navigation starts, while
  // `resolvedLocation` only catches up in a layout effect once the new
  // matches have committed. So `/sign-in` → `/` would drop `I18nProvider`
  // while sign-in is still mounted, and `/` → `/sign-in` would render
  // sign-in before it mounts. Requiring BOTH to be marketing keeps the
  // providers up for the whole transition and unmounts them only once the
  // app route is genuinely gone.
  const onAppRoute = useRouterState({
    select: (state) =>
      !(
        isMarketingPath(state.location.pathname) &&
        isMarketingPath((state.resolvedLocation ?? state.location).pathname)
      ),
  })

  // …and once they are up they stay up, because consulting both locations
  // narrowed that teardown window without closing it. The two move in
  // separate commits, so an ordering where `resolvedLocation` reaches the
  // marketing path while the app route is still in the tree drops
  // `I18nProvider` under a live component — `useLingui()` returns null and
  // the route error boundary catches a crash on a page the user already
  // left (PLANNO-WEB-7).
  //
  // Latching costs nothing that the split was protecting: the point is that
  // anonymous visitors never DOWNLOAD the app shell, and this only pins
  // providers for a session that has already loaded the chunk. Leaving an
  // app route back to marketing now keeps the upload queue alive too, which
  // matches why it sits above the auth boundary in the first place.
  // First render latches nothing, so the prerender and hydration still agree.
  const hasEnteredApp = useRef(false)
  if (onAppRoute) {
    hasEnteredApp.current = true
  }
  const needsAppShell = onAppRoute || hasEnteredApp.current

  // The English marketing pages are prerendered as separate documents, so the
  // language belongs in the static HTML rather than being patched in after
  // hydration. Everything else defaults to pt-BR and, on app routes, gets
  // refined by `localeScript` from the stored preference.
  const lang = useRouterState({
    select: (state) => {
      const path = state.location.pathname
      return path === '/en' || path.startsWith('/en/') ? 'en' : 'pt-BR'
    },
  })

  return (
    /* The blocking theme/locale scripts intentionally patch attributes before
       React hydrates. Suppress that one known root-level difference; without
       it every public page logs a false hydration error in dark mode. */
    <html data-app-density={onAppRoute ? '' : undefined} lang={lang} suppressHydrationWarning>
      <head>
        {/* biome-ignore lint/security/noDangerouslySetInnerHtml: blocking theme script to prevent FOUC */}
        <script dangerouslySetInnerHTML={{ __html: themeScript }} />
        {/* biome-ignore lint/security/noDangerouslySetInnerHtml: blocking locale script to set <html lang> */}
        <script dangerouslySetInnerHTML={{ __html: localeScript }} />
        <HeadContent />
      </head>
      <body>
        {needsAppShell ? (
          <Suspense fallback={null}>
            <AppShellProviders>{children}</AppShellProviders>
          </Suspense>
        ) : (
          children
        )}
        <Scripts />
      </body>
    </html>
  )
}
